import { ExclamationCircleFilled } from '@ant-design/icons'
import { GraviButton, Horizontal, Texto, Vertical, addCommasToNumber } from '@gravitate-js/excalibrr'
import { Form, InputNumber, Popconfirm, Radio } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { ByCompartmentRow } from './ByCompartmentRow'

export function Products({
  selectedSite,
  volumeError,
  setVolumeError,
  formattedVolume,
  productsList,
  maxNumberOfProducts,
  requestedProducts,
  setRequestedProducts,
  compartmentDefaultVolumes,
  form,
  handleCompartmentChange,
  byProductEntry,
  setByProductEntry,
  setTotalVolume,
  setMaxNumberOfProducts,
}) {
  const [popVisible, setPopVisible] = useState(false)
  const changeEntryType = () => {
    setByProductEntry(!byProductEntry)
    setMaxNumberOfProducts(false)
    setVolumeError('')
    setRequestedProducts([])
    setTotalVolume(0)
    form.setFieldsValue({ totalVolume: 0 })
    const fields = form.getFieldsValue()
    for (const field in fields) {
      if (field.includes('product') || field.includes('compartment')) {
        form.setFieldsValue({ [field]: null })
      }
    }
  }
  const compartments = useMemo(() => compartmentDefaultVolumes || [], [compartmentDefaultVolumes])

  const checkEntryChange = () => {
    if (requestedProducts?.length > 0) {
      setPopVisible(true)
    } else {
      changeEntryType()
    }
  }

  const title = (
    <Vertical className='p-1'>
      <Texto>Are you sure you want to change the entry type?</Texto>
      <Texto>This will clear all the entered volumes.</Texto>
    </Vertical>
  )

  if (!selectedSite) return null

  return (
    <Vertical gap={16}>
      <Horizontal verticalCenter>
        <Vertical>
          <Texto category='h5'>Volume By</Texto>
        </Vertical>
        <Horizontal verticalCenter>
          {selectedSite.allow_by_product_request ? (
            <Popconfirm
              title={title}
              visible={popVisible}
              onConfirm={() => {
                setPopVisible(false)
                changeEntryType()
              }}
              onCancel={() => setPopVisible(false)}
              okText='Yes'
              cancelText='No'
              icon={<ExclamationCircleFilled style={{ color: 'var(--theme-error)' }} />}
            >
              <Radio.Group buttonStyle='solid' value={byProductEntry} onChange={checkEntryChange}>
                <Radio.Button value={false}>Compartment</Radio.Button>
                <Radio.Button value>Product</Radio.Button>
              </Radio.Group>
            </Popconfirm>
          ) : (
            <GraviButton className='m-0' theme2 buttonText='Compartment' />
          )}
        </Horizontal>
      </Horizontal>

      {byProductEntry ? (
        <Vertical>
          <Horizontal className='bg-2 bordered py-2 px-3' borderRadius='6px 6px 0 0'>
            <Vertical flex={1}>
              <Texto category='h6' textTransform='uppercase'>
                PRODUCT
              </Texto>
            </Vertical>
            <Vertical flex={1}>
              <Texto category='h6'>SITE ISLAND</Texto>
            </Vertical>
            <Vertical flex={2}>
              <Texto category='h6' align='right'>
                REQUESTED VOLUME
              </Texto>
            </Vertical>
          </Horizontal>
          {productsList?.map((product, index) => (
            <ByProductRow
              key={index}
              index={index}
              product={product}
              formattedVolume={formattedVolume}
              volumeError={volumeError}
              setVolumeError={setVolumeError}
              maxNumberOfProducts={maxNumberOfProducts}
              requestedProducts={requestedProducts}
            />
          ))}
        </Vertical>
      ) : (
        <Vertical>
          <Horizontal className='bg-2 bordered py-2 px-3' borderRadius='6px 6px 0 0'>
            <Vertical flex={1}>
              <Texto category='h6' textTransform='uppercase'>
                Trailer Compartment
              </Texto>
            </Vertical>
            <Vertical flex={1} alignItems='center' verticalCenter>
              <Texto category='h6' textTransform='uppercase' align='right'>
                Requested Volume
              </Texto>
            </Vertical>
            <Vertical flex={1.5}>
              <Texto category='h6' align='right' textTransform='uppercase'>
                Product
              </Texto>
            </Vertical>
          </Horizontal>
          {compartments?.map((_, index) => (
            <ByCompartmentRow
              key={index}
              compartmentDefaultVolumes={compartmentDefaultVolumes}
              index={index}
              productsList={productsList}
              formattedVolume={formattedVolume}
              requestedProducts={requestedProducts}
              compartments={compartments}
              form={form}
              handleCompartmentChange={handleCompartmentChange}
            />
          ))}
        </Vertical>
      )}
    </Vertical>
  )
}

function ByProductRow({
  formattedVolume,
  product,
  index,
  volumeError,
  setVolumeError,
  maxNumberOfProducts,
  requestedProducts,
}) {
  const [isEditing, setIsEditing] = useState(true)

  const volume = useMemo(() => {
    const requestedProduct = requestedProducts?.find(
      (requestedProduct) => requestedProduct.product_id === product.product_id
    )
    const result = requestedProduct ? requestedProduct.quantity : null
    if (result <= 0) {
      setIsEditing(true)
    }
    return result
  }, [requestedProducts])
  const isDisabled = useMemo(() => !volume && maxNumberOfProducts, [volume, maxNumberOfProducts])
  const border = useMemo(
    () =>
      volumeError === 'max_volume' || volumeError === 'min_volume' ? '1px solid red' : '1px solid var(--gray-200)',
    [volumeError, volume]
  )
  useEffect(() => {
    const requestedProductVolume = requestedProducts?.find(
      (requestedProduct) => requestedProduct.product_id === product?.product_id
    )?.quantity
    if (requestedProductVolume > 0) setIsEditing(false)
  }, [])

  return (
    <Horizontal key={index} className='bordered p-3' verticalCenter>
      <Vertical flex={1}>
        <Texto category='p2'>{product.product_name}</Texto>
      </Vertical>
      <Vertical flex={1}>
        <Texto textTransform='capitalize' category='p2'>
          {product.island}
        </Texto>
      </Vertical>
      <Vertical flex={2} alignItems='end'>
        <Form.Item className='m-0' name={`product ${product.product_id} ${product.island}`}>
          {isEditing ? (
            <InputNumber
              onFocus={() => {
                if (volumeError === 'max_volume') setVolumeError('')
              }}
              style={{ width: 115 }}
              placeholder='Enter Volume'
              min={0}
              value={volume}
              onBlur={() => {
                if (volume > 0) setIsEditing(false)
              }}
              disabled={isDisabled}
            />
          ) : (
            <div
              onClick={() => setIsEditing(true)}
              className='bordered p-2 pl-3'
              style={{ width: 115, borderRadius: '5px', border }}
            >
              <Texto textTransform='lowercase' category='h5'>
                {addCommasToNumber(volume)} {formattedVolume}s
              </Texto>
            </div>
          )}
        </Form.Item>
      </Vertical>
    </Horizontal>
  )
}
